import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { unassignTrip } from '../services';
import type { Trip } from '../types';

interface TripRow {
  id: string;
  business_id: string;
  project_id: string | null;
  driver_id: string | null;
  start_time: string;
  end_time: string | null;
  start_address_text: string | null;
  end_address_text: string | null;
  distance_km: number | null;
  distance_source: string | null;
  rate_per_km: number | null;
  calculated_cost: number | null;
  billable: boolean;
  billing_status: string;
  assignment_status: string;
  suggested_project_id: string | null;
  match_location: string | null;
  match_confidence: string | null;
  match_distance_m: number | null;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
  driver_name: string | null;
  project_name: string | null;
  customer_name: string | null;
  suggested_project_name: string | null;
  suggested_customer_name: string | null;
}

function mapRowToTrip(row: TripRow): Trip {
  return {
    id: row.id,
    businessId: row.business_id,
    projectId: row.project_id,
    driverId: row.driver_id,
    startTime: row.start_time,
    endTime: row.end_time,
    startAddressText: row.start_address_text,
    endAddressText: row.end_address_text,
    distanceKm: row.distance_km,
    distanceSource: row.distance_source,
    ratePerKm: row.rate_per_km,
    calculatedCost: row.calculated_cost,
    billable: row.billable,
    billingStatus: row.billing_status as Trip['billingStatus'],
    assignmentStatus: row.assignment_status as Trip['assignmentStatus'],
    suggestedProjectId: row.suggested_project_id,
    matchLocation: row.match_location as Trip['matchLocation'],
    matchConfidence: row.match_confidence as Trip['matchConfidence'],
    matchDistanceM: row.match_distance_m,
    processedAt: row.processed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    driverName: row.driver_name,
    projectName: row.project_name,
    customerName: row.customer_name,
    suggestedProjectName: row.suggested_project_name,
    suggestedCustomerName: row.suggested_customer_name,
  };
}

interface UseProjectTripsReturn {
  trips: Trip[];
  isLoading: boolean;
  error: string | null;
  unassignTrip: (tripId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useProjectTrips(projectId: string | null): UseProjectTripsReturn {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!projectId) {
      setTrips([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('v_trips_with_assignment')
        .select('*')
        .eq('project_id', projectId)
        .order('start_time', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setTrips((data || []).map(mapRowToTrip));
    } catch (err) {
      console.error('Error fetching project trips:', err);
      setError(err instanceof Error ? err.message : 'Failed to load trips');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUnassignTrip = useCallback(async (tripId: string) => {
    await unassignTrip(tripId);
    // Remove from local state
    setTrips((prev) => prev.filter((t) => t.id !== tripId));
  }, []);

  return {
    trips,
    isLoading,
    error,
    unassignTrip: handleUnassignTrip,
    refetch: fetchData,
  };
}